/* eslint-disable max-lines-per-function */
import { bytesToNDigits } from '@mail/cross-sizes-utils';
import heavyFileTransactDark from 'img/heavy-file-byte/transact-dark.jpg';
import heavyFileTransactLight from 'img/heavy-file-byte/transact-light.jpg';
import heavyFileUsualDark from 'img/heavy-file-byte/usual-dark.jpg';
import heavyFileUsualLight from 'img/heavy-file-byte/usual-light.jpg';
import React, { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { abHeavyFileBuy, abHeavyFileBuyV3 } from 'reactApp/appHelpers/featuresHelpers/features/heavyFileBuy';
import { uploadBigFileModalAb } from 'reactApp/appHelpers/featuresHelpers/features/uploadBigFileModal';
import { uploadBigFileNewText } from 'reactApp/appHelpers/featuresHelpers/features/uploadBigFileNewText';
import { useTariffUploadBigFileModalAb } from 'reactApp/components/UploadBigFileDialog/hooks/useTariffUploadBigFileModalAb';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { initProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import Content from 'reactApp/ui/Content/Content';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { useShouldShowFreemiumHeavyFile } from 'reactApp/ui/Freemium/useShouldShowFreemiumHeavyFile';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { createGaSender } from 'reactApp/utils/ga';

import { WhatsNewDialog } from '../WhatsNewDialog/WhatsNewDialog';
import { useBigFileModalCallbacks } from './hooks/useBigFileModalCallbacks';
import { useContent } from './hooks/useContent';
import { useTariff } from './hooks/useTariff';
import { useTariffHeavyFileBuyAb } from './hooks/useTariffHeavyFileBuyAb';
import styles from './UploadBigFileDialog.css';
import type { BigFileModalStage, Props, TariffInfo } from './UploadBigFileDialog.types';
import { UploadBizBigFileDialog } from './UploadBizBigFileDialog';

const sendGa = createGaSender('upload-big-file');
const dataQAId = 'upload-big-file';

const UploadB2CBigFileDialog = React.memo(function UploadB2CBigFileDialog(props: Props): ReactElement {
    const dispatch = useDispatch();
    const [stage, setStage] = useState<BigFileModalStage>('usual');
    const isDarkTheme = useSelector(isCurrentDarkTheme);

    const { numberOfFiles, onClose: closeModal, skipFileLoading, continueFileLoading, file, files } = props;
    const fileSizeBytes = file?.sizeBytes || files?.sizeBytes;

    const tariffInfoBase: TariffInfo = useTariff(fileSizeBytes || 0);
    /* tempexp_17604-next-line */
    const tariffInfoUploadBigFileModalAb = useTariffUploadBigFileModalAb(fileSizeBytes || 0);
    /* tempexp_18869-next-line */
    const tariffInfoHeavyFileBuyAb = useTariffHeavyFileBuyAb('v2', tariffInfoBase);
    /* tempexp_18955-next-line */
    const tariffInfoHeavyFileBuyAbV3 = useTariffHeavyFileBuyAb('v3', tariffInfoBase);

    let tariffInfo: TariffInfo;
    switch (true) {
        /* tempexp_18955-start */
        case Boolean(abHeavyFileBuyV3):
            tariffInfo = tariffInfoHeavyFileBuyAbV3;
            break;
        /* tempexp_18955-end */

        /* tempexp_18869-start */
        case Boolean(abHeavyFileBuy):
            tariffInfo = tariffInfoHeavyFileBuyAb;
            break;
        /* tempexp_18869-end */

        /* tempexp_17604-start */
        case uploadBigFileModalAb === 'b':
            tariffInfo = tariffInfoUploadBigFileModalAb;
            break;
        /* tempexp_17604-end */

        default:
            tariffInfo = tariffInfoBase;
    }

    const callbacks = useBigFileModalCallbacks({
        stage,
        closeModal,
        nextStage: () => {
            if (stage === 'usual') {
                setStage('transact');
            }
        },
        skipFileLoading,
        continueFileLoading,
        sendGa,
        tariffInfo,
        fileSizeBytes,
    });
    const modalContent = useContent({ tariffInfo, numberOfFiles, file, files, callbacks, stage });
    const header = useMemo(
        () => (
            <div data-qa-id="title" className={styles.header}>
                {modalContent.header}
            </div>
        ),
        [modalContent.header]
    );

    const productsState = useSelector(ProductsSelectors.getLifeCycleState);
    const isPhone = EnvironmentSelectors.isPhone();
    const shouldShowFreemium = useShouldShowFreemiumHeavyFile();

    useEffect(() => {
        dispatch(initProducts());
    }, []);

    useEffect(() => {
        callbacks.sendAnalytics('view');

        sendGa('show', tariffInfo.showTariffType);
        // sendPaymentGa({ action: EPaymentGa.bigFileModal, label: 'view', size_offer: fileSizeBytes });
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_SHOW, { stage });
    }, [stage]);

    const content = useMemo(
        () => (
            <>
                <div data-qa-id="content" className={styles.content}>
                    {modalContent.annotation}
                </div>
                {/* tempexp_18869-start tempexp_18955-start */}
                {!(abHeavyFileBuy || abHeavyFileBuyV3) && !shouldShowFreemium && (
                    <div className={styles.fileContainer}>
                        {Boolean(file) && (
                            <div className={styles.fileIcon}>
                                <FileIcon size="xs" type={file?.extension} />
                            </div>
                        )}
                        <div className={styles.description}>
                            <span className={styles.fileDescription}>{modalContent.description}</span>
                            <span className={styles.fileSize}>
                                {uploadBigFileNewText ? '' : '0 КБ из '}
                                {bytesToNDigits(file?.sizeBytes || files?.sizeBytes || 0, 3).value}
                            </span>
                        </div>
                    </div>
                )}
                {/* tempexp_18869-end tempexp_18955-end */}
                {modalContent.actions}
            </>
        ),
        [file, files?.sizeBytes, modalContent.actions, modalContent.annotation, modalContent.description]
    );

    if (isPhone) {
        return (
            <MobileDialog
                className={styles.root_phone}
                id={dataQAId}
                open
                dimmer
                onClose={callbacks.onClose}
                disableScrolling
                mod="confirm"
                title={header}
                topmost
            >
                {content}
            </MobileDialog>
        );
    }

    /* tempexp_18955-start */
    let image;
    if (abHeavyFileBuyV3 === 'd') {
        if (stage === 'usual') {
            image = isDarkTheme ? heavyFileUsualDark : heavyFileUsualLight;
        } else if (stage === 'transact') {
            image = isDarkTheme ? heavyFileTransactDark : heavyFileTransactLight;
        }
    }
    /* tempexp_18955-end */

    return (
        <WhatsNewDialog
            hideImage={!image}
            imageUrl={image}
            primaryButtonText=""
            titleClassName={styles.header_web}
            title={<div data-qa-id="title">{modalContent.header}</div>}
            qaId={dataQAId}
            className={styles.root}
            onDismiss={callbacks.onClose}
            dialogSize="tiny"
            content={
                <Content isModal isLoading={productsState.isLoading}>
                    {content}
                </Content>
            }
        />
    );
});

export const UploadBigFileDialog = React.memo(function UploadBigFileDialog(props: Props): ReactElement {
    const isBiz = useSelector(UserSelectors.isBizUser);

    return isBiz ? <UploadBizBigFileDialog {...props} /> : <UploadB2CBigFileDialog {...props} />;
});
